import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';

const REPLY_TO_RE = /^[^\s@<>,;:"()[\]\\]+@[a-z0-9-]+(\.[a-z0-9-]+)+$/i; // mirrors server/replyTo.js

// The client's default Reply-To for campaigns/newsletters — where replies land,
// independent of the From (#192: send from info@news.brand.com, answers reach
// info@brand.com). Same component for both surfaces (dual-surface rule): Admin →
// client detail (scope 'admin') and the client's own Settings (scope 'my'). On
// 'my' a 403 means engage.reply_to is off, and the field simply isn't shown.
export default function ReplyToField({ entityId, scope = 'admin' }) {
  const [d, setD] = useState(null);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState(false);
  const hydrate = (v) => { setD(v); setValue(v.replyTo || ''); };
  useEffect(() => { setD(null); setErr(''); setSaved(false); api.getReplyTo(entityId, scope).then(hydrate).catch(() => {}); }, [entityId, scope]);
  if (!d) return null;

  const bad = !!value.trim() && !REPLY_TO_RE.test(value.trim());
  const dirty = value.trim() !== (d.replyTo || '');
  const save = () => {
    if (bad) return;
    setBusy(true); setErr(''); setSaved(false);
    api.saveReplyTo(entityId, value.trim(), scope).then((v) => { hydrate(v); setSaved(true); }).catch((e) => setErr(e.message)).finally(() => setBusy(false));
  };
  const input = { padding: '7px 10px', minHeight: 40, boxSizing: 'border-box', borderRadius: 8, border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--text)', fontSize: 13 };

  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--hairline)' }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 4 }}>↩ Reply-to address</div>
      <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5, marginBottom: 8 }}>
        Where replies to your campaigns and newsletters land, if that's not the address you send from — e.g. send from <code>info@news.yourbrand.com</code>, receive replies at <code>info@yourbrand.com</code>. Leave blank and replies go to the From address. A campaign can still set its own.
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="email" inputMode="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} maxLength={254}
          value={value} onChange={(e) => { setValue(e.target.value); setSaved(false); }}
          placeholder="Same as the From address" aria-label="Reply-to address"
          style={{ ...input, flex: '1 1 220px', minWidth: 0 }}
        />
        <button onClick={save} disabled={busy || bad || !dirty} style={{ ...input, cursor: 'pointer', fontWeight: 600 }}>{busy ? '…' : 'Save'}</button>
      </div>
      {bad && <div style={{ fontSize: 12, color: 'var(--error,#ef4444)', marginTop: 6 }}>That isn't a valid email address — use a full address like info@yourbrand.com</div>}
      {err && <div style={{ fontSize: 12, color: 'var(--error,#ef4444)', marginTop: 6 }}>{err}</div>}
      {saved && !dirty && <div style={{ fontSize: 12, color: '#15803d', marginTop: 6 }}>✓ Saved — {d.replyTo ? <>replies now go to <b>{d.replyTo}</b></> : 'replies go to the From address'}.</div>}
      {scope === 'admin' && d.enabled === false && <div style={{ fontSize: 12, color: '#b45309', marginTop: 6 }}>The Reply-To flag (engage.reply_to) is off for this client — this is saved but not applied until it's switched on in Admin → Product → Flags.</div>}
    </div>
  );
}
